import { useSSchedulerStore } from '@lp/core/store/scheduler';
import {
  useMonthScheduleStates,
  useResourceReservationsToDayMaps,
  useScheduleControls,
} from '@lp/features/Scheduler/hooks';
import {
  HrSystemConflictsDrawerModal,
  SchedulerControlHeader,
  SchedulerLoadingOverlay,
  PublishBlockerTooltip,
} from '@lp/features/Scheduler/components';
import {
  useHtLuSSchedulerData,
  useHtLuSSchedulerServices,
  useHtLuSWorkdaySync,
  useLiSSchedulerData,
  useLiSSchedulerServices,
  useLiSWorkdaySync,
  useHtLuSShiftChange,
  useLiSShiftChange,
  useLiSUnblockShift,
  useHtLuSUnblockShift,
  useSScheduleValidation,
  useLiSSchedulerMutationActions,
  useHtLuSSchedulerMutationActions,
  useSSchedulerShiftHighlight,
  useSFilteredRegionVisibility,
  useSAutoExpandOnFilterChange,
} from '@lp/features/Scheduler/SchedulerSurgeons/hooks';
import {
  RegionWrapper,
  SHrSystemConflictCard,
  SSchedulerControlRow,
  SSchedulerTable,
} from '@lp/features/Scheduler/SchedulerSurgeons/components';
import { SOrganGroup } from '@lp/features/Scheduler/SchedulerSurgeons/types';
import { useAuthContext } from '@lp/core/auth';
import { SchedulerPermissions } from '@/permissions';

type SchedulerSurgeonsProps = {
  organGroup: SOrganGroup;
  settingsPath: string;
  disableSettings?: boolean;
  readonly?: boolean;
  useSSchedulerData: typeof useLiSSchedulerData | typeof useHtLuSSchedulerData;
  useSSchedulerMutationActions: typeof useLiSSchedulerMutationActions | typeof useHtLuSSchedulerMutationActions;
  useSSchedulerServices: typeof useLiSSchedulerServices | typeof useHtLuSSchedulerServices;
  useSShiftChange: typeof useLiSShiftChange | typeof useHtLuSShiftChange;
  useSUnblockShift: typeof useLiSUnblockShift | typeof useHtLuSUnblockShift;
  useSWorkdaySync: typeof useLiSWorkdaySync | typeof useHtLuSWorkdaySync;
};

export const SchedulerSurgeons = ({
  organGroup,
  settingsPath,
  disableSettings,
  readonly,
  useSSchedulerData,
  useSSchedulerServices,
  useSShiftChange,
  useSUnblockShift,
  useSWorkdaySync,
  useSSchedulerMutationActions,
}: SchedulerSurgeonsProps) => {
  const regionAssignedSurgeonsMap = useSSchedulerStore.use.assignedSurgeonsMap()[organGroup];
  const {
    startDay,
    selectedDate,
    selectedDateRange,
    daysInterval,
    onMonthPrev,
    onMonthNext,
    onDateChange,
    isReservationsView,
    setIsReservationsView,
  } = useScheduleControls();
  const {
    shiftsData,
    isShiftsLoading,
    monthSchedule,
    isPlanningMode,
    togglePlanningMode,
    planningData,
    reservationsData,
  } = useSSchedulerData({ startDay, dateRange: selectedDateRange, isReservationsView, isPlanningInitial: !readonly });
  const { regionList, regionToResourceMap, regionsDictionary } = useSSchedulerServices(
    shiftsData,
    regionAssignedSurgeonsMap,
  );
  const regionVisibility = useSFilteredRegionVisibility(organGroup, regionList, regionToResourceMap);
  useSAutoExpandOnFilterChange(regionVisibility);
  const { visibleRegionIds, hasResourceFilters } = regionVisibility;
  const reservationsToDayMaps = useResourceReservationsToDayMaps(reservationsData);
  const onShiftChange = useSShiftChange(selectedDateRange, monthSchedule, isPlanningMode);
  const { checkPermission } = useAuthContext();
  const canUnblockShifts = checkPermission(SchedulerPermissions.SSchedulesFixationTypeWrite);
  const onUnblockShift = useSUnblockShift(selectedDateRange, monthSchedule, isPlanningMode);
  const workdayData = useSWorkdaySync(startDay, selectedDateRange, monthSchedule, isPlanningMode, readonly);
  const actions = useSSchedulerMutationActions(startDay, planningData);
  // Validation is temporary hidden in scope of TMDXLP-6259
  const validationData = useSScheduleValidation(planningData, shiftsData, regionsDictionary, isPlanningMode);
  const { isEditable, isGenerating, isResettable } = useMonthScheduleStates(monthSchedule, isPlanningMode, readonly);
  const highlightResourceEmptyShift = useSSchedulerShiftHighlight({
    organGroup,
    selectedDate,
    daysInterval,
    regionToResourceMap,
    shiftsData,
  });
  const showBackdrop = isGenerating || isShiftsLoading;

  if (!shiftsData || !regionList) return null;

  return (
    <div className="flex flex-col min-h-full">
      <div className="p-6 pb-2 sticky top-0 z-12 bg-base-70">
        <SchedulerControlHeader
          readonly={readonly}
          disableAutogeneration
          hidePlanningToggle
          disableSettings={disableSettings}
          settingsPath={settingsPath}
          actions={actions}
          monthSchedule={monthSchedule}
          planningData={planningData}
          isPlanningMode={isPlanningMode}
          togglePlanningMode={togglePlanningMode}
          // violationsCount={validationData?.count}
          // conflictsButton={<ConflictsDrawerModal tabsData={sConflictsDrawerData} validationData={validationData} />}
          hrConflictsButton={
            <HrSystemConflictsDrawerModal hrSystemData={workdayData} ConflictCard={SHrSystemConflictCard} />
          }
          validationIsFetching={validationData?.isFetching}
          publishReadinessData={validationData?.publishReadiness}
          fillingState={validationData?.fillingState}
          publishBlockerTooltip={
            <PublishBlockerTooltip
              shiftsData={shiftsData}
              publishReadinessData={validationData?.publishReadiness}
              onResourceClick={highlightResourceEmptyShift}
            />
          }
          startDay={startDay}
          selectedDate={selectedDate}
          onMonthPrev={onMonthPrev}
          onMonthNext={onMonthNext}
        >
          <SSchedulerControlRow
            organGroup={organGroup}
            readonly={readonly}
            selectedDate={selectedDate}
            shiftsData={shiftsData}
            workdayData={workdayData}
            onDateChange={onDateChange}
            isReservationsView={isReservationsView}
            setIsReservationsView={setIsReservationsView}
          />
        </SchedulerControlHeader>
      </div>
      <div className="flex flex-col gap-6 relative p-6 pt-4 h-full">
        {showBackdrop && (
          <SchedulerLoadingOverlay
            isGenerating={isGenerating}
            isMutating={actions.isMutating}
            generationInfo={planningData?.autoSchedulerInfo}
            onGenerationCancel={actions.onGenerationCancel}
          />
        )}

        {hasResourceFilters && visibleRegionIds.size === 0 && (
          <div className="flex items-center justify-center h-full">
            <p className="text-l-m text-base-35">No results for applied filters</p>
          </div>
        )}

        {regionList.map((region, regionIndex) => {
          if (hasResourceFilters && !visibleRegionIds.has(region.id)) return null;

          const userList = regionToResourceMap?.[region.id] ?? [];
          // const regionViolations = validationData?.byOrganRegionMap[organGroup]?.[region.id];

          return (
            <RegionWrapper
              key={region.id}
              region={region}
              readonly={readonly}
              organGroup={organGroup}
              userList={userList}
              shiftsData={shiftsData}
              regionsDictionary={regionsDictionary}
              initiallyExpanded={hasResourceFilters || regionIndex === 0}
              // violations={regionViolations}
            >
              <SSchedulerTable
                region={region}
                organGroup={organGroup}
                selectedDate={selectedDate}
                daysInterval={daysInterval}
                userList={userList}
                shiftsData={shiftsData}
                reservationsMap={reservationsToDayMaps}
                regionsDictionary={regionsDictionary}
                isEditable={isEditable}
                isReservationsView={isReservationsView}
                canResetShifts={isResettable}
                canUnblockShifts={canUnblockShifts}
                // regionViolations={regionViolations}
                // resourceViolations={validationData?.byResourceMap}
                onShiftChange={onShiftChange}
                onUnblockShift={canUnblockShifts ? onUnblockShift : undefined}
              />
            </RegionWrapper>
          );
        })}
      </div>
    </div>
  );
};
